import { Link } from 'react-router-dom';

import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import { css } from '@emotion/react';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import { BooleanFilter } from '@e-socna/utils';
import type {
  FragmentType,
  CivilityId,
  GenericStatusId,
  UserRoleId,
  RepresentativeTypeId,
} from '@e-socna/data-layer';

import { PATH_PERSON_DEFAULT } from '../../app/constants/paths.constant';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useBuildPath } from '../../hooks/useBuildPath.hook';
import { N_A } from '../../constants/labels.constant';

import { GenericStatusIndicator } from '../GenericStatusIndicator';
import { Avatar } from '../Avatar';
import { thirdPartyTooltipOf } from '../../utils/third-party-tooltip.util';

export const OrganizationMembershipCardCmp_OrganizationMembership_Fragment =
  graphql(`
    fragment OrganizationMembershipCardCmp_OrganizationMembership_Fragment on OrganizationMembership {
      person {
        _id
        civilityId
        color
        fullName
        pictureImageUri
        statusId
      }
      functions
      contextualRoleIds
      representativeTypeIds
    }
  `);

interface Props {
  readonly membership: FragmentType<
    typeof OrganizationMembershipCardCmp_OrganizationMembership_Fragment
  >;
}

export const OrganizationMembershipCard = ({
  membership: pMembership,
}: Readonly<Props>) => {
  const { representativeTypesIdMap, userRolesIdMap } = useReferencesContext();

  const { person, functions, contextualRoleIds, representativeTypeIds } =
    getFragmentData(
      OrganizationMembershipCardCmp_OrganizationMembership_Fragment,
      pMembership,
    );

  const path = useBuildPath(PATH_PERSON_DEFAULT, { id: person._id });

  const { civilitiesIdMap } = useReferencesContext();

  const civilityLabel =
    civilitiesIdMap[person.civilityId as CivilityId]?.label ?? 'Inconnu';

  const roles = contextualRoleIds
    ?.map((roleId) => userRolesIdMap[roleId as UserRoleId]?.label)
    .filter(BooleanFilter) ?? ['Inconnu'];

  const representativeTypes = representativeTypeIds
    ?.map(
      (representativeTypeId) =>
        representativeTypesIdMap[representativeTypeId as RepresentativeTypeId]
          ?.label,
    )
    .filter(BooleanFilter) ?? ['Inconnu'];

  return (
    <Grid
      size={{
        lg: 3,
        md: 6,
        xs: 12,
      }}
      sx={{ flexGrow: 1 }}
    >
      <Card>
        <CardHeader
          action={
            <GenericStatusIndicator
              statusId={person.statusId as GenericStatusId}
              variant="icon"
            />
          }
          avatar={
            <Avatar
              bgColor={person.color}
              fullName={person.fullName}
              imageUri={person.pictureImageUri}
              label={thirdPartyTooltipOf(person.fullName)}
            />
          }
          css={css`
            .MuiCardHeader-action {
              display: contents;
            }
          `}
        />
        <CardContent>
          <Typography
            component="div"
            gutterBottom
            sx={{ lineHeight: 1.2 }}
            variant="h6"
          >
            {person.fullName}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            {civilityLabel}
          </Typography>
          <Typography gutterBottom variant="body2">
            Fonction(s) occupée(s) :
            <br />
            {functions.length ? functions.slice().sort().join(', ') : N_A}
          </Typography>
          <Typography gutterBottom variant="body2">
            Rôle(s) e-SOCNA :
            <br />
            {roles.length ? roles.slice().sort().join(', ') : N_A}
          </Typography>
          <Typography gutterBottom variant="body2">
            Type(s) de représentant :
            <br />
            {representativeTypes.length
              ? representativeTypes.slice().sort().join(', ')
              : N_A}
          </Typography>
        </CardContent>
        <CardActions>
          <Button component={Link} size="small" to={path} variant="outlined">
            Consulter
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );
};
